import { useState, useEffect, useCallback, Fragment } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, ShieldAlert, ChevronRight, ChevronDown, ExternalLink, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

const SOURCE_BADGE = {
  DOB: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  ECB: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  HPD: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

// Human labels for the raw detail keys shown in the expanded row.
const DETAIL_LABELS = {
  violation_number: 'Violation #', violation_type: 'Type', violation_category: 'Category',
  issue_date: 'Issue Date', disposition_date: 'Disposition Date', description: 'Description',
  address: 'Address', status_label: 'Status', severity: 'Severity', hearing_status: 'Hearing Status',
  ecb_violation_status: 'ECB Status', dataset_id: 'Dataset', source: 'Source',
  violationid: 'Violation ID', novid: 'NOV ID', ordernumber: 'Order #', violationstatus: 'Violation Status',
  currentstatus: 'Current Status', currentstatusdate: 'Current Status Date', novissueddate: 'NOV Issued',
  inspectiondate: 'Inspection Date', class: 'Class', novdescription: 'Description', novtype: 'NOV Type',
  rentimpairing: 'Rent Impairing',
};
const HIDDEN_DETAIL_KEYS = new Set(['is_open']);

function labelFor(k) {
  return DETAIL_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function ComplianceViolationsTab({ onOpenProject, mismatchProjectIds = new Set() }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ open: 0, resolved: 0, rent_impairing: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all | open | resolved | rent
  const [expanded, setExpanded] = useState(null);

  const fetchViolations = useCallback(async () => {
    try {
      const r = await api.get('/compliance/violations');
      setRows(r.data.rows || []);
      setSummary(r.data.summary || { open: 0, resolved: 0, rent_impairing: 0 });
    } catch { setRows([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchViolations(); }, [fetchViolations]);

  const filtered = rows.filter(v => {
    if (sourceFilter !== 'all' && v.source !== sourceFilter) return false;
    if (statusFilter === 'open' && !v.is_open) return false;
    if (statusFilter === 'resolved' && v.is_open) return false;
    if (statusFilter === 'rent' && !v.rent_impairing) return false;
    if (search) {
      const q = search.toLowerCase();
      return (v.project_number || '').toLowerCase().includes(q)
        || (v.project_name || '').toLowerCase().includes(q)
        || (v.address || '').toLowerCase().includes(q)
        || (v.number || '').toLowerCase().includes(q)
        || (v.source || '').toLowerCase().includes(q)
        || (v.source_full || '').toLowerCase().includes(q);
    }
    return true;
  });

  const toggleStatus = (s) => setStatusFilter(prev => (prev === s ? 'all' : s));

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">Loading violations...</p>;

  if (rows.length === 0) {
    return (
      <Card className="border-border bg-card shadow-none">
        <CardContent className="py-12 text-center">
          <ShieldAlert className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No violations recorded across any project yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Add violations per-project from the Building/Violations Profile tab using NYC Open Data discovery.</p>
        </CardContent>
      </Card>
    );
  }

  const cards = [
    { key: 'open', label: 'Open', value: summary.open, dot: 'bg-rose-400', active: statusFilter === 'open' },
    { key: 'resolved', label: 'Resolved / Dismissed', value: summary.resolved, dot: 'bg-emerald-400', active: statusFilter === 'resolved' },
    { key: 'rent', label: 'Rent-Impairing (HPD)', value: summary.rent_impairing, dot: 'bg-amber-400', active: statusFilter === 'rent' },
  ];

  return (
    <div className="space-y-3">
      {/* Summary cards (click to filter) */}
      <div className="grid grid-cols-3 gap-2">
        {cards.map(c => (
          <Card
            key={c.key}
            className={`border-border shadow-none cursor-pointer transition-colors hover:border-primary/30 ${c.active ? 'border-primary/50 bg-primary/5' : 'bg-card'}`}
            onClick={() => toggleStatus(c.key)}
            data-testid={`violations-card-${c.key}`}
          >
            <CardContent className="p-2.5 text-center">
              <div className={`h-2 w-2 rounded-full mx-auto mb-1 ${c.dot}`} />
              <p className="text-lg font-bold">{c.value}</p>
              <p className="text-xs text-muted-foreground leading-tight">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search + source filter (matches Filings pattern) */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search project, address, source, #..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border w-72" data-testid="violations-search" />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-40 bg-card border-border text-xs h-9" aria-label="Filter by source" data-testid="violations-source-filter">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Sources</SelectItem>
            <SelectItem value="DOB" className="text-xs">DOB</SelectItem>
            <SelectItem value="ECB" className="text-xs">ECB</SelectItem>
            <SelectItem value="HPD" className="text-xs">HPD</SelectItem>
          </SelectContent>
        </Select>
        {(search || sourceFilter !== 'all' || statusFilter !== 'all') && (
          <button className="text-xs text-primary hover:underline ml-1" onClick={() => { setSearch(''); setSourceFilter('all'); setStatusFilter('all'); }}>Clear filters</button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} of {rows.length} violations</span>
      </div>

      {/* Combined table */}
      <Card className="border-border bg-card shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Address</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Source</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Violation / Ticket #</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Class</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Issue Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground text-xs py-8">No violations match your filters.</TableCell></TableRow>
              ) : filtered.map((v, i) => {
                const open = expanded === i;
                return (
                  <Fragment key={i}>
                    <TableRow className="border-border hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => setExpanded(open ? null : i)} data-testid={`violations-row-${i}`}>
                      <TableCell className="text-xs">
                        <span className="inline-flex items-center gap-1 flex-wrap">
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                          <span className="font-mono text-primary">{v.project_number || '—'}</span>
                          <span className="text-muted-foreground ml-1 hidden lg:inline truncate max-w-[160px]">{v.project_name}</span>
                          {mismatchProjectIds.has(v.project_id) && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-400/90 border border-amber-500/30 rounded px-1.5 py-0.5 font-sans whitespace-nowrap" title="This project's BIN may not match its address on file" data-testid={`violations-bin-mismatch-${i}`}>
                              <AlertCircle className="h-2.5 w-2.5" /> BIN mismatch
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{v.address || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs border ${SOURCE_BADGE[v.source] || 'border-border text-muted-foreground'}`} data-testid={`violations-source-${i}`}>{v.source}</Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {v.number || '-'}
                        {v.rent_impairing && <Badge variant="outline" className="ml-1.5 text-[10px] border-amber-500/40 text-amber-400">Rent-Impairing</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{v.hpd_class || ''}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs border ${v.is_open ? 'border-rose-500/40 text-rose-400' : 'border-emerald-500/40 text-emerald-400'}`} data-testid={`violations-status-${i}`}>{v.status_label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{v.issue_date || '-'}</TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className="border-border bg-secondary/20 hover:bg-secondary/20">
                        <TableCell colSpan={7} className="p-0">
                          <div className="px-4 py-3" data-testid={`violations-detail-${i}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{v.source_full} — Record Detail</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onOpenProject?.(v.project_id, 'building-profile'); }}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                data-testid={`violations-view-in-project-${i}`}
                              >
                                <ExternalLink className="h-3 w-3" /> View in Project
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                              {Object.entries(v.detail || {})
                                .filter(([k, val]) => !HIDDEN_DETAIL_KEYS.has(k) && val !== null && val !== '' && val !== undefined)
                                .map(([k, val]) => (
                                  <div key={k} className="flex gap-2 text-xs">
                                    <span className="text-muted-foreground shrink-0 min-w-[120px]">{labelFor(k)}</span>
                                    <span className="text-foreground break-words">{String(val)}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
